import { Box, CircularProgress, Collapse, useMediaQuery } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { useIntl } from 'react-intl';
import { useDebounceValue } from 'usehooks-ts';
import { ConnectorNextError } from '~/backend/connector-next-api/connector-next-fetcher';
import { useClinicalListsListPeople } from '~/backend/connector-next-api/connector-next-components';
import { useConnectorIdentity } from '~/hooks/auth/useConnectorIdentity';
import { useIsOpeningInViewer } from '~/hooks/openInViewer';
import { useElementSize } from '~/hooks/useElementSize';
import { useToday } from '~/hooks/useToday';
import { ImportWizard } from './import';
import PatientDrilldown from './PatientDrilldown';
import PatientList from './PatientList';
import { LIST_MIN_WIDTH_COMPACT } from './PatientList/PatientListTable';
import PatientListFilterBar from './PatientListFilterBar';
import PatientListToolbar from './PatientListToolbar';
import { ListPane, ListSurface, ResizeHandle } from './shared';
import {
    useClinicalLists,
    usePatientList,
    type PatientListState,
} from './hooks';
import {
    clearGroup,
    hasActivePatientFilters,
    nextSort,
    NO_PATIENT_FILTERS,
    toggleFilterValue,
    withAssignedToUserId,
    type PatientFilterFacetId,
    type PatientFilterGroup,
    type SortValue,
} from './model';

const SEARCH_DEBOUNCE_MS = 300;

// Versioned: the panel's default width grew, and a width stored under the
// previous key would hold a returning user at the old, narrower panel.
const PANEL_WIDTH_KEY = 'patient-list-panel-width-v2';
/**
 * The panel carries the patient's whole record — identity, course, and a row
 * per dataset with its facts — so it takes the larger share of the screen,
 * and the list beside it narrows to the patient and their stage.
 */
const DEFAULT_PANEL_WIDTH = 1040;
const MIN_PANEL_WIDTH = 720;
/**
 * Never below the compact table's own minimum, or the list would be laid out
 * wider than the card holding it and the end of the strip would be clipped.
 */
const MIN_LIST_WIDTH = LIST_MIN_WIDTH_COMPACT;
/** Gap between the list card and the panel card; the resize seam lives in it. */
const PANE_GAP = 20;
/** How long the outgoing column set stays over the incoming one while it fades. */
const LAYOUT_CROSSFADE_MS = 200;
const fadeOut = keyframes`
    from { opacity: 1; }
    to { opacity: 0; }
`;

/** A column set and the width it was laid out at, kept while it fades out. */
type ListLayout = { compact: boolean; width: number };

function readStoredPanelWidth(): number {
    try {
        const stored = Number(localStorage.getItem(PANEL_WIDTH_KEY));
        if (Number.isFinite(stored) && stored >= MIN_PANEL_WIDTH) {
            return stored;
        }
    } catch {
        // Local storage can be unavailable; the default is fine.
    }
    return DEFAULT_PANEL_WIDTH;
}

/**
 * The All patients view: toolbar, patient list, and the docked per-patient
 * drilldown panel. Its query state (search, sort, filters, selection) is held
 * by the caller, so it survives the view being left and shown again.
 */
export default function PatientListScreen({
    tabs,
    state,
}: {
    /** The list's views, beside the title. */
    tabs?: ReactNode;
    state: PatientListState;
}) {
    const intl = useIntl();
    const {
        searchInput,
        setSearchInput,
        sort,
        setSort,
        filters,
        setFilters,
        selectedId,
        setSelectedId,
        scopeRef,
    } = state;
    // The patient the panel is built for: the selection while there is one,
    // and the last one while the panel slides shut, so it closes showing its
    // content. Caught up during render rather than in each handler that
    // changes the selection, so none of them can forget it; cleared once the
    // slide has ended.
    const [panelPatientId, setPanelPatientId] = useState<string>();
    if (selectedId !== undefined && selectedId !== panelPatientId) {
        setPanelPatientId(selectedId);
    }
    // True from the moment the panel starts sliding until it has settled.
    const [panelSliding, setPanelSliding] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [panelWidth, setPanelWidth] = useState(readStoredPanelWidth);
    const paneRowRef = useRef<HTMLDivElement>(null);
    const [search] = useDebounceValue(searchInput.trim(), SEARCH_DEBOUNCE_MS);
    // Debounced like search: the menu stays open for multi-select, so raw
    // filters would issue one query per checkbox click.
    const [debouncedFilters] = useDebounceValue(filters, SEARCH_DEBOUNCE_MS);
    // Where focus goes when the control that had it disappears with the
    // filters it cleared.
    const filterControlRef = useRef<HTMLButtonElement>(null);
    // An open filter menu is a modal that hides the live region below it.
    const [filterMenuOpen, setFilterMenuOpen] = useState(false);
    const { data: lists } = useClinicalLists();
    const { data: people } = useClinicalListsListPeople({});
    const today = useToday();
    const openBusy = useIsOpeningInViewer();

    // A PACS retrieval that arrived whole selects the patient it landed in, so the drilldown opens
    // on what was just imported. Set rather than toggled: this is not the user clicking the row,
    // and a toggle would close the panel when the retrieved patient was already the selected one.
    const openRetrievedPatient = useCallback(
        (patientId: string) => setSelectedId(patientId),
        [setSelectedId]
    );
    // The element that opened the panel; closing hands focus back to it.
    const restoreFocusRef = useRef<HTMLElement>();

    const {
        patients,
        totalCount,
        hasMore,
        loadMore,
        isLoadingMore,
        isLoading,
        isRefreshing,
        isError,
        error,
    } = usePatientList({
        search: search || undefined,
        sort,
        filters: debouncedFilters,
    });

    const handleSelect = useCallback(
        (id: string) => {
            restoreFocusRef.current =
                document.activeElement instanceof HTMLElement
                    ? document.activeElement
                    : undefined;
            setSelectedId((current) => (current === id ? undefined : id));
        },
        [setSelectedId]
    );

    // The synchronous focus() runs while the panel is still mounted, so focus
    // never lands on <body>; a row unmounted by a refetch is left alone.
    const closePanel = useCallback(() => {
        setSelectedId(undefined);
        const target = restoreFocusRef.current;
        if (target?.isConnected) {
            target.focus();
        }
    }, [setSelectedId]);

    const handleSort = useCallback(
        (key: SortValue) => {
            setSort((current) => nextSort(current, key));
        },
        [setSort]
    );

    const focusFilterControl = useCallback(() => {
        filterControlRef.current?.focus();
    }, []);

    const handleFilterToggle = useCallback(
        (facetId: PatientFilterFacetId, value: string) =>
            setFilters((current) => toggleFilterValue(current, facetId, value)),
        [setFilters]
    );

    // The synchronous focus() runs before the activated clear control
    // unmounts with the selection it cleared, so focus never lands on <body>.
    const handleGroupClear = useCallback(
        (group: PatientFilterGroup) => {
            focusFilterControl();
            setFilters((current) => clearGroup(current, group));
        },
        [focusFilterControl, setFilters]
    );

    const clearFilters = useCallback(() => {
        focusFilterControl();
        setFilters(NO_PATIENT_FILTERS);
    }, [focusFilterControl, setFilters]);

    // "Assigned to me" is offered only where the connector identifies its
    // users, which is what lets a patient be taken on, and only to one of them.
    const { signInEnabled, canAssignPatients: canAssign } =
        useConnectorIdentity();
    const meId = people?.meId ?? undefined;
    const handleAssignedToMeChange = useMemo(
        () =>
            meId === undefined
                ? undefined
                : (assignedToMe: boolean) =>
                      setFilters((current) =>
                          withAssignedToUserId(
                              current,
                              assignedToMe ? meId : undefined
                          )
                      ),
        [meId, setFilters]
    );

    const clearSearchAndFilters = useCallback(() => {
        focusFilterControl();
        setFilters(NO_PATIENT_FILTERS);
        setSearchInput('');
    }, [focusFilterControl, setFilters, setSearchInput]);

    // Search narrows the same query as the filters, so every "this list is
    // not the cohort" surface keys on both. Derived from the debounced values
    // the query uses, so it describes the presented result.
    const isNarrowed =
        search !== '' || hasActivePatientFilters(debouncedFilters);

    // A changed search or filter re-scopes the list; a selection made under
    // the old scope must not present a patient the new one excludes.
    useEffect(() => {
        const scope = scopeRef.current;
        if (scope.search !== search || scope.filters !== debouncedFilters) {
            scopeRef.current = { search, filters: debouncedFilters };
            setSelectedId(undefined);
        }
    }, [search, debouncedFilters, scopeRef, setSelectedId]);

    const openImport = useCallback(() => setImportOpen(true), []);

    const closeImport = useCallback(() => setImportOpen(false), []);

    const panelOpen = selectedId !== undefined;

    // Escape closes the patient panel, but never while the import dialog is
    // open (it owns its own Escape) nor while an open is in flight. The open's
    // dialogs are the ones that own Escape then, and the incomplete-retrieval
    // dialog deliberately does not consume the key, so a listener here would
    // close the panel out from underneath an unanswered clinical question and
    // take the selection the answer applies to with it.
    useEffect(() => {
        if (!panelOpen || importOpen || openBusy) {
            return;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closePanel();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [panelOpen, importOpen, openBusy, closePanel]);

    // Tracks the space the two panes share, so the panel can never be dragged — or restored from
    // a wider session — past the point where the list still has room to be read.
    const paneRowWidth = useElementSize(paneRowRef)?.widthPx ?? 0;

    const maxPanelWidth = Math.max(
        MIN_PANEL_WIDTH,
        paneRowWidth - PANE_GAP - MIN_LIST_WIDTH
    );
    // Clamped for display only: a narrow window never overwrites the stored preference.
    const effectivePanelWidth = Math.min(panelWidth, maxPanelWidth);
    // The list takes the column set of the panel's target state at once and,
    // while the panel slides, is laid out at the width it will have when the
    // slide ends, its card clipping it: the columns and timelines never
    // reflow on a frame of the slide. At rest the flex layout gives the same
    // width, so nothing moves when the slide ends.
    const compact = panelOpen;
    const listWidthWithPanel = Math.max(
        0,
        paneRowWidth - PANE_GAP - effectivePanelWidth
    );
    const listWidth =
        panelSliding && paneRowWidth > 0
            ? compact
                ? listWidthWithPanel
                : paneRowWidth
            : undefined;
    // A column-set switch cannot be animated, so it is crossfaded: the
    // outgoing set stays painted over the incoming one, at the width it had,
    // and fades out. It needs the row's measurement to lay that width out,
    // and is skipped under reduced motion.
    const { transitions } = useTheme();
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [shownCompact, setShownCompact] = useState(compact);
    const [outgoingLayout, setOutgoingLayout] = useState<ListLayout>();
    if (compact !== shownCompact) {
        setShownCompact(compact);
        if (paneRowWidth > 0 && !reducedMotion) {
            setOutgoingLayout({
                compact: shownCompact,
                width: shownCompact ? listWidthWithPanel : paneRowWidth,
            });
        }
    }
    useEffect(() => {
        if (outgoingLayout === undefined) {
            return;
        }
        const timer = setTimeout(
            () => setOutgoingLayout(undefined),
            LAYOUT_CROSSFADE_MS
        );
        return () => clearTimeout(timer);
    }, [outgoingLayout]);
    const listScrollRef = useRef<HTMLDivElement>(null);

    const persistPanelWidth = useCallback((width: number) => {
        const rounded = Math.round(width);
        setPanelWidth(rounded);
        try {
            localStorage.setItem(PANEL_WIDTH_KEY, String(rounded));
        } catch {
            // Local storage can be unavailable; the width still applies for this session.
        }
    }, []);

    const errorMessage = useMemo(() => {
        if (!isError) {
            return undefined;
        }
        // ConnectorNextError.message is the server's fixed `title` (PHI-safe);
        // anything else gets generic copy.
        return error instanceof ConnectorNextError
            ? error.message
            : intl.formatMessage({ id: 'patientList.error.load' });
    }, [isError, error, intl]);

    // Everything the list shows apart from its column set, which the live
    // list and its fading copy take separately.
    const listProps = {
        patients,
        totalCount,
        hasMore,
        onLoadMore: loadMore,
        isLoadingMore,
        isLoading,
        errorMessage,
        selectedId,
        onSelect: handleSelect,
        sort,
        onSortChange: handleSort,
        isNarrowed,
        onClearSearchAndFilters: clearSearchAndFilters,
        lists,
        people,
        canAssign,
        today,
    };

    return (
        <>
            <PatientListToolbar
                totalCount={isLoading || isError ? undefined : totalCount}
                isNarrowed={isNarrowed}
                searchInput={searchInput}
                onSearchInputChange={setSearchInput}
                isFetching={isRefreshing}
                onImport={openImport}
                tabs={tabs}
            >
                <PatientListFilterBar
                    assignedToMe={
                        signInEnabled
                            ? {
                                  pressed:
                                      filters.assignedToUserId !== undefined,
                                  onChange: handleAssignedToMeChange,
                              }
                            : undefined
                    }
                    filters={filters}
                    configured={lists}
                    onToggle={handleFilterToggle}
                    onClearGroup={handleGroupClear}
                    onClear={clearFilters}
                    firstControlRef={filterControlRef}
                    onMenuOpenChange={setFilterMenuOpen}
                />
            </PatientListToolbar>
            <ImportWizard
                open={importOpen}
                onClose={closeImport}
                onRetrieved={openRetrievedPatient}
            />
            {/* Screen-reader-only echo of the narrowed count: chips and footer
                convey it visually only. Announced only once the query has
                settled (a placeholder total would misstate the match count)
                and no filter menu is open, since an open menu is a modal that
                removes this subtree from the accessibility tree. */}
            <Box role="status" aria-live="polite" sx={visuallyHidden}>
                {isNarrowed &&
                !isLoading &&
                !isRefreshing &&
                !isError &&
                !filterMenuOpen
                    ? intl.formatMessage(
                          { id: 'patientList.filter.matchCount' },
                          { totalCount }
                      )
                    : ''}
            </Box>
            <ListPane ref={paneRowRef}>
                <ListSurface>
                    <Box
                        ref={listScrollRef}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                            width: listWidth,
                        }}
                    >
                        <PatientList {...listProps} compact={compact} />
                    </Box>
                    {outgoingLayout && (
                        <Box
                            aria-hidden
                            // Scrolled as far as the list it covers, so the
                            // same rows fade into each other.
                            ref={(overlay: HTMLDivElement | null) => {
                                if (overlay) {
                                    overlay.scrollTop =
                                        listScrollRef.current?.scrollTop ?? 0;
                                }
                            }}
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                bottom: 0,
                                width: outgoingLayout.width,
                                overflow: 'hidden',
                                bgcolor: 'background.default',
                                pointerEvents: 'none',
                                animation: `${fadeOut} ${LAYOUT_CROSSFADE_MS}ms ease-out forwards`,
                            }}
                        >
                            {/* Never loads: its sentinel would request
                                the same page as the live list's. */}
                            <PatientList
                                {...listProps}
                                compact={outgoingLayout.compact}
                                hasMore={false}
                            />
                        </Box>
                    )}
                    {isRefreshing && (
                        <Box
                            aria-hidden
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                pt: 6,
                                bgcolor: (theme) =>
                                    alpha(
                                        theme.palette.background.default,
                                        0.4
                                    ),
                                pointerEvents: 'none',
                            }}
                        >
                            <CircularProgress size={22} />
                        </Box>
                    )}
                </ListSurface>

                {/* The panel slides in from the right and back out, the list
                    card narrowing and widening with it instead of snapping:
                    the slot sits at the row's right edge, so its left edge is
                    what moves and the panel moves with it. Once open the
                    slot's width follows its content, so dragging the seam
                    still resizes the panel. */}
                <Collapse
                    in={panelOpen}
                    orientation="horizontal"
                    unmountOnExit
                    timeout={
                        reducedMotion
                            ? 0
                            : {
                                  enter: transitions.duration.enteringScreen,
                                  exit: transitions.duration.leavingScreen,
                              }
                    }
                    onEnter={() => setPanelSliding(true)}
                    onEntered={() => setPanelSliding(false)}
                    onExit={() => setPanelSliding(true)}
                    onExited={() => {
                        setPanelSliding(false);
                        setPanelPatientId(undefined);
                    }}
                    sx={{
                        flexShrink: 0,
                        '& .MuiCollapse-wrapperInner': { display: 'flex' },
                    }}
                >
                    {panelPatientId !== undefined && (
                        <>
                            <ResizeHandle
                                value={effectivePanelWidth}
                                min={MIN_PANEL_WIDTH}
                                max={maxPanelWidth}
                                onChange={setPanelWidth}
                                onCommit={persistPanelWidth}
                                pane="after"
                                width={PANE_GAP}
                            />
                            {/* key remounts the panel per patient so tab/selection
                                state can never carry (even for one frame) across
                                patients. */}
                            <PatientDrilldown
                                key={panelPatientId}
                                patientId={panelPatientId}
                                width={effectivePanelWidth}
                                onClose={closePanel}
                            />
                        </>
                    )}
                </Collapse>
            </ListPane>
        </>
    );
}
